import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ShieldCheck, ArrowRight, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const SESSION_KEY = "warden_exit_shown";

export const ExitIntentModal = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const trigger = useCallback(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(true);
  }, []);

  useEffect(() => {
    const onLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };
    document.addEventListener("mouseleave", onLeave);
    // Mobile / no-mouse fallback: fire after a dwell period
    const timer = setTimeout(trigger, 40000);
    return () => {
      document.removeEventListener("mouseleave", onLeave);
      clearTimeout(timer);
    };
  }, [trigger]);

  const copyCode = () => {
    navigator.clipboard?.writeText("LAUNCH30");
    setCopied(true);
    toast.success("Code LAUNCH30 copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const claim = () => {
    setOpen(false);
    navigate("/signup");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-intent-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#030812]/80 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <motion.div
            data-testid="exit-intent-modal"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: "spring", bounce: 0.18, duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl border border-cyanbrand/40 bg-[#0A0F1D] p-8 overflow-hidden"
            style={{ boxShadow: "0 0 60px rgba(0,229,255,0.18)" }}
          >
            <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-cyanbrand/15 blur-3xl" />
            <button
              data-testid="exit-intent-close"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-cyanbrand border border-cyanbrand/30 rounded-full px-3 py-1 bg-cyanbrand/5">
                <ShieldCheck size={13} /> Before you go
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-5 leading-tight">
                Wait — take <span className="text-cyanbrand">30% off</span> your first 3 months
              </h2>
              <p className="text-slate-400 text-sm mt-3 leading-relaxed">
                Secure your website today. Start a free 7-day trial (no card required) and lock in
                launch pricing with the code below.
              </p>

              <button
                data-testid="exit-intent-code"
                onClick={copyCode}
                className="mt-6 w-full flex items-center justify-between gap-3 rounded-lg border border-dashed border-cyanbrand/50 bg-cyanbrand/5 px-4 py-3 group"
              >
                <span className="font-mono text-lg font-bold tracking-[0.25em] text-cyanbrand">LAUNCH30</span>
                <span className="flex items-center gap-1.5 text-xs text-slate-400 group-hover:text-white transition-colors">
                  {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                  {copied ? "Copied" : "Tap to copy"}
                </span>
              </button>

              <button
                data-testid="exit-intent-claim"
                onClick={claim}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-cyanbrand hover:bg-[#33ECFF] text-slate-900 font-bold px-6 py-3 rounded-xl transition-all"
                style={{ boxShadow: "0 0 24px rgba(0,229,255,0.4)" }}
              >
                Claim 30% off <ArrowRight size={16} />
              </button>
              <p className="text-center text-[11px] text-slate-500 mt-3">
                30-day money-back guarantee · cancel anytime
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
